import { ApiError } from '@lib/api/api-response';
import { isApiError } from '@lib/api/utils/is-api-error';
import { isNonRetryableError } from '@lib/api/utils/is-non-retryable-error';
import {
  type ClerkRefreshOutcome,
  type RefreshClerkSessionOptions,
  getClerkSession,
  isClerkRefreshSessionUsable,
  refreshClerkSession,
} from '@lib/auth/clerk-session-bridge';
import { STALE_TIME } from '@lib/constants/apis';
import { captureTanstackError } from '@lib/observability/errors/capture-tanstack-error';
import { shouldSuppressEntitlementDeniedError } from '@lib/observability/errors/should-suppress-entitlement-denied-error';
import { shouldSuppressSentryForMeta } from '@lib/observability/errors/should-suppress-sentry-for-meta';
import { shouldSuppressSignedOutQueryError } from '@lib/observability/errors/should-suppress-signed-out-query-error';
import { observeStaleHydratedCacheError } from '@lib/observability/stale-hydration-tags/observe-stale-hydrated-cache.client';
import { isBrowserFetchTransportError } from '@lib/shared/utils/is-browser-fetch-transport-error';

import type { ExpectedQueryFailure } from './expected-query-failure';
import { MutationCache, QueryCache, QueryClient } from './tanstack-reexports';
import { isQueryAuthLossClearEnabled } from './utils/is-query-auth-loss-clear-enabled';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: AppQueryMeta;
    mutationMeta: AppMutationMeta;
  }
}

/** Shared meta flags for queries and mutations across the app. */
export interface AppQueryMeta {
  /** Skip the global TanStack→Sentry capture for these `ApiError` statuses. */
  suppressSentryStatuses?: number[];
  /**
   * Skip the global TanStack→Sentry capture when `error.name` matches one of
   * these strings. Use for user-facing errors already handled at the call site
   * (e.g. SSE stream error frames surfaced via toast).
   */
  suppressSentryErrorNames?: string[];
  /**
   * Opt out of the global Clerk 401 refresh — e.g. for token-flow endpoints
   * that don't authenticate via Clerk. See `.rulesync/rules/data-layer.md`.
   */
  skipClerkRefresh?: boolean;
  /**
   * Declares an expected failure class for this query. The global
   * Sentry/stale-cache pipeline maps it to HTTP status — hooks do
   * not list status codes for authz outcomes handled in UI.
   */
  expectedFailure?: ExpectedQueryFailure;
}

export interface AppMutationMeta extends AppQueryMeta {
  /**
   * Opt in: re-run this mutation once after a Clerk refresh on a 401.
   * Only safe for idempotent mutations (PUT, DELETE, idempotent PATCH).
   * Default is refresh-only to avoid double-creating on non-idempotent
   * endpoints.
   */
  autoRetryOnClerkRefresh?: boolean;
}

// Per-cache-entry deadline for the next allowed Clerk refresh. The cooldown
// breaks the infinite-refresh loop on a persistent 401: `query.fetch()`
// resets `fetchFailureCount` so every retry looks like a fresh first failure,
// and `MutationCache.onError` runs before mutation `failureCount` is
// dispatched — neither cache offers a reliable "first failure" signal.
// WeakMap entries are GC'd with the Query/Mutation, so legitimate future
// refreshes after a real recovery still work.
const refreshCooldownUntil = new WeakMap<object, number>();
const REFRESH_COOLDOWN_MS = 30_000;
const REFRESH_COOLDOWN_FAILURE_MS = 5_000;

function shouldAttemptClerkRefresh(
  error: unknown,
  meta: AppQueryMeta | undefined,
  cacheEntry: object
): boolean {
  if (!(error instanceof ApiError) || error.status !== 401) return false;
  if (meta?.skipClerkRefresh === true) return false;
  const deadline = refreshCooldownUntil.get(cacheEntry) ?? 0;
  if (Date.now() < deadline) return false;
  return true;
}

function stampCooldown(cacheEntry: object, outcome: ClerkRefreshOutcome): void {
  const cooldownMs =
    outcome.status === 'refreshed'
      ? REFRESH_COOLDOWN_MS
      : REFRESH_COOLDOWN_FAILURE_MS;
  refreshCooldownUntil.set(cacheEntry, Date.now() + cooldownMs);
}

function shouldRefetchAfterClerkRefresh(outcome: ClerkRefreshOutcome): boolean {
  // Late Clerk hydration uses `resolveClerkReady`'s 500ms wait:
  // a session before the wait ends yields `refreshed` and refetch.
  // Refetch after proven `no-session` re-storms 401s on signed-out
  // /login tabs with stale authed cache.
  return isClerkRefreshSessionUsable(outcome);
}

function observeStaleHydratedCacheIfNeeded(
  error: unknown,
  query: {
    queryKey: readonly unknown[];
    meta: AppQueryMeta | undefined;
    state: {
      data: unknown;
      dataUpdatedAt: number;
      fetchFailureCount: number;
    };
  },
  clerkRefreshAttempted: boolean
): void {
  if (query.state.data === undefined) return;
  if (error instanceof Error) {
    const meta = query.meta as Record<string, unknown> | undefined;
    if (shouldSuppressSentryForMeta(error, meta)) return;
    if (shouldSuppressSignedOutQueryError(error, meta)) return;
    if (shouldSuppressEntitlementDeniedError(error)) return;
    // Connectivity TypeErrors / status-0 ApiErrors are not app faults —
    // keep the stale-cache path quiet (PORTAL-WY / PORTAL-99 / PORTAL-WV /
    // issue 7693570517).
    if (isBrowserFetchTransportError(error)) return;
  }

  const apiStatus = error instanceof ApiError ? error.status : undefined;
  const dataUpdatedAt = query.state.dataUpdatedAt;
  const dataAgeMs =
    dataUpdatedAt > 0 ? Math.max(0, Date.now() - dataUpdatedAt) : 0;
  observeStaleHydratedCacheError({
    sourceKey: firstStringSegment(query.queryKey),
    apiStatus,
    failureCount: query.state.fetchFailureCount,
    dataAgeMs,
    clerkRefreshAttempted,
  });
}

/**
 * Best-effort first cache-key segment for Sentry's `clerk_refresh.source_key`
 * tag — lets the dashboard group "which features keep stranding the
 * session". Only returns the segment when it's safely stringifiable
 * (string or number); objects / arrays / nullish segments yield undefined
 * so the bridge omits the tag entirely.
 */
function firstStringSegment(
  key: readonly unknown[] | undefined
): string | undefined {
  const first = key?.[0];
  if (typeof first === 'string') return first;
  if (typeof first === 'number') return String(first);
  return undefined;
}

function resolveClerkReady(
  options: RefreshClerkSessionOptions
): Promise<ClerkRefreshOutcome> {
  if (getClerkSession()) {
    return refreshClerkSession(options);
  }
  // No session yet (Clerk still initializing): wait for hydration, then
  // re-check via refreshClerkSession so a late session is refreshed and a
  // still-missing session records `no-session` for signed-out Sentry policy.
  return new Promise<ClerkRefreshOutcome>(resolve => {
    setTimeout(() => {
      void refreshClerkSession(options).then(resolve);
    }, 500);
  });
}

function createQueryCache(): QueryCache {
  return new QueryCache({
    onError: (error, query) => {
      const clerkRefreshAttempted = shouldAttemptClerkRefresh(
        error,
        query.meta,
        query
      );

      // `meta.suppress*` opts a query out of every Sentry path, so a
      // query that already declares its 401/403s expected (e.g. the
      // health poll) stays silent here too. On the Clerk-refresh path,
      // defer until refresh settles — `isCallerProvablySignedOut` is
      // false until then, so a synchronous check would false-positive.
      if (clerkRefreshAttempted) {
        void resolveClerkReady({
          trigger: '401_query',
          sourceKey: firstStringSegment(query.queryKey),
        }).then(outcome => {
          stampCooldown(query, outcome);
          const skipStaleObserve =
            outcome.status === 'no-session' &&
            error instanceof Error &&
            shouldSuppressSignedOutQueryError(error, query.meta);
          if (!skipStaleObserve) {
            observeStaleHydratedCacheIfNeeded(error, query, true);
          }
          if (shouldRefetchAfterClerkRefresh(outcome)) {
            void query.fetch().catch(() => undefined);
          }
        });
        return;
      }

      observeStaleHydratedCacheIfNeeded(error, query, false);

      captureTanstackError(error, 'tanstack_query', {
        queryKey: query.queryKey,
        meta: query.meta,
        fetchStatus: query.state.fetchStatus,
        failureCount: query.state.fetchFailureCount,
      });
    },
  });
}

function createMutationCache(): MutationCache {
  return new MutationCache({
    onError: (error, variables, _onMutateResult, mutation) => {
      if (shouldAttemptClerkRefresh(error, mutation.meta, mutation)) {
        const optIn = mutation.meta?.autoRetryOnClerkRefresh === true;
        void resolveClerkReady({
          trigger: '401_mutation',
          sourceKey: firstStringSegment(mutation.options.mutationKey),
        }).then(outcome => {
          stampCooldown(mutation, outcome);
          if (optIn && shouldRefetchAfterClerkRefresh(outcome)) {
            void mutation.execute(variables).catch(() => undefined);
          }
        });
        return;
      }

      captureTanstackError(error, 'tanstack_mutation', {
        mutationKey: mutation.options.mutationKey,
        variables,
        meta: mutation.meta,
        failureCount: mutation.state.failureCount,
      });
    },
  });
}

// Allow one native retry for 401. While TanStack is in its retry window the
// query stays in 'pending' state — the component never sees an error flash.
// A 500 ms delay gives Clerk enough time to re-hydrate its session cookie
// (e.g. after a dev fast-refresh that resets the module-level QueryClient
// singleton). If the retry also returns 401, the query errors and
// QueryCache.onError runs the Clerk-refresh fallback path.
const retryForQuery = (failureCount: number, error: unknown): boolean => {
  if (isApiError(error) && error.status === 401 && failureCount < 1) {
    return true;
  }
  if (isNonRetryableError(error)) return false;
  return failureCount < 3;
};

const retryDelayForQuery = (attemptIndex: number, error: unknown): number => {
  if (isApiError(error) && error.status === 401) return 500;
  return Math.min(1000 * 2 ** attemptIndex, 30000);
};

// Create a client instance
export const queryClient = new QueryClient({
  queryCache: createQueryCache(),
  mutationCache: createMutationCache(),
  defaultOptions: {
    queries: {
      gcTime: STALE_TIME,
      staleTime: 1000 * 60 * 1,
      retry: retryForQuery,
      retryDelay: retryDelayForQuery,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: (failureCount, error) => {
        if (isNonRetryableError(error)) return false;
        return failureCount < 1;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

// Create a new query client instance (for server-side rendering and QueryProvider).
// Pass { isServer: true } from getQueryClient() to disable retries on the server:
// SSR prefetch failures should fall through immediately to client-side fetches
// rather than spending ~7s on exponential-backoff retries before giving up.
export const createQueryClient = ({
  isServer = false,
}: { isServer?: boolean } = {}) =>
  new QueryClient({
    queryCache: createQueryCache(),
    mutationCache: createMutationCache(),
    defaultOptions: {
      queries: {
        gcTime: STALE_TIME,
        staleTime: 1000 * 60 * 1,
        retry: isServer ? false : retryForQuery,
        retryDelay: retryDelayForQuery,
        refetchOnWindowFocus: !isServer,
        refetchOnReconnect: !isServer,
      },
    },
  });

// Browser-side singleton for hydration
let browserQueryClient: QueryClient | undefined = undefined;

export function bindBrowserQueryClient(client: QueryClient): void {
  if (typeof window === 'undefined') return;
  browserQueryClient = client;
}

/**
 * Get a QueryClient for server-side prefetching and hydration.
 * On the server, always creates a new client.
 * On the browser, reuses the existing client for proper cache sharing.
 */
export function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    // Server: always create a new client with retries disabled
    return createQueryClient({ isServer: true });
  }
  // Browser: reuse existing client
  if (!browserQueryClient) {
    browserQueryClient = createQueryClient();
  }
  return browserQueryClient;
}

/**
 * Cancel in-flight TanStack work before a 401 → login redirect.
 *
 * Intentionally does **not** call `client.clear()`:
 * - `clear()` notifies mounted observers and can start fresh
 *   unauthenticated fetches (see use-anon-claim).
 * - Call sites hard-navigate (`location.href`); if a `beforeunload`
 *   guard aborts that navigation, an emptied cache would leave the
 *   user on the protected page with no data. Hard unload replaces the
 *   JS realm when navigation completes, so clear is redundant.
 */
export async function clearQueryCacheBeforeAuthRedirect(): Promise<void> {
  if (!isQueryAuthLossClearEnabled()) return;
  if (typeof window === 'undefined') return;
  const client = getQueryClient();
  await client.cancelQueries();
}
